import moment from 'moment'
import 'moment-duration-format'

const DEFAULT_DATETIME_FORMAT = 'ddd MMM DD HH:mm:ss YYYY'
const DEFAULT_UTC = false
const SEVERITY_THRESHHOLDS = {
  low: 0,
  medium: 33,
  high: 66
}

const DEFAULT_HUMAN_DURATION_FORMAT = {
  template: 'y [y] M[mo] D[d] H[h] m[m] s[s]',
  precision: 2
}

const NUMBER_LABELS = ['', 'k', 'm', 'bil', 'tril']
const DURATION_LABELS = ['ms', 's', 'm', 'h', 'd', 'w', 'm', 'y']
const DURATIONS = [
  1,
  1000,
  60000,
  60000 * 60, // 1 hour
  60000 * 60 * 24, // 1 day
  60000 * 60 * 24 * 7, // 1 week
  60000 * 60 * 24 * 30, // 1 month
  60000 * 60 * 24 * 365, // 1 year
  Infinity
]

// Adapted from http://stackoverflow.com/a/18650828
const _formatBytes = (
  inputBytes,
  convertBytesToBitsPerSec,
  detailPrecision = 0
) => {
  // Binary format should output IEC standard "kibibit' prefixes "
  const sizePrefixes = ['', 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y']
  const sizeSuffix = convertBytesToBitsPerSec ? 'b/s' : 'B'
  let output = {
    textData: '0',
    label: sizeSuffix
  }

  if (!isNaN(inputBytes) && inputBytes > 0) {
    const input = convertBytesToBitsPerSec ? inputBytes * 8 : inputBytes
    const kBase = 1000
    const i = Math.floor(Math.log(input) / Math.log(kBase))
    const val = input / Math.pow(kBase, i)
    const precision =
      val > 100 ? detailPrecision : val > 10 ? 1 : detailPrecision
    output = {
      // textData: (input / Math.pow(kBase, i)).toPrecision(3)
      textData: val.toFixed(precision),
      label: `${sizePrefixes[i] || ''}${sizeSuffix}`
    }
  }
  return output
}


// Use formatBytesToBits to format input bytes into a base10 string format, converted to a bit-rate. eg (1024 Bytes = 8.192 kilobits/sec)
export const formatBytesToBits = (
  bytes,
  returnConcat = false,
  detailPrecision
) => {
  const result = _formatBytes(bytes, true, detailPrecision)
  return returnConcat ? `${result.textData} ${result.label || ''}` : result
}

// Use formatBytes to format input bytes into a base2 string format. eg (1024 Bytes = 1 kilobyte)
export const formatBytes = (bytes, returnConcat = true, detailPrecision) => {
  const result = _formatBytes(bytes, false, detailPrecision)
  return returnConcat ? `${result.textData} ${result.label || ''}` : result
}

export const formatNumber = (number, concat = false, sigFigs = 3) => {
  let out = {}
  if (number < 1000) {
    out = {
      textData: String(number),
      label: NUMBER_LABELS[0]
    }
  } else {
    const base = Math.log(number) / Math.log(1000)
    const floorBase = Math.floor(base)
    const label = NUMBER_LABELS[floorBase]
    const val = Math.pow(1000, base - floorBase)
    out = {
      textData: val.toPrecision(sigFigs),
      label: label
    }
  }
  return concat ? `${out.textData}${out.label}` : out
}

// return number of milliseconds formatted as a decimal  (e.g. 12.56d)
export const formatDuration = (
  millis,
  concat = false,
  sigFigs = 3,
  labels = DURATION_LABELS
) => {
  for (var i = 0, _len = DURATIONS.length; i < _len; i++) {
    if (millis < DURATIONS[i]) {
      const out = {
        textData: (millis / DURATIONS[i - 1]).toPrecision(sigFigs),
        label: labels[i - 1]
      }
      if (out.textData && out.label) {
        return concat ? `${out.textData}${out.label}` : out
      } else {
        return millis
      }
    }
  }
}

// return number of milliseconds formatted as a human-readable duration (e.g. 12d 13h 41m)
export const formatDurationHuman = (
  millis,
  format = DEFAULT_HUMAN_DURATION_FORMAT
) => {
  return moment.duration(millis).format(format)
}

export const formatDate = (
  timestamp,
  format = DEFAULT_DATETIME_FORMAT,
  utc = DEFAULT_UTC
) => {
  if (!timestamp || timestamp == null) {
    return false
  }
  // D3 translations:
  // %X = HH:mm:ss
  // %x = MM/DD/YYYY
  // %a = ddd

  // Special handling for "iso" string
  if (format === 'iso') {
    format = null
  }
  // if (utc || _isUTCMode) {
  if (utc) {
    return moment.utc(timestamp).format(format)
  } else {
    return moment(timestamp).format(format)
  }
}

export const formatRelativeTime = (timestamp, utc = DEFAULT_UTC) => {
  // let _time = utc || _isUTCMode ? moment.utc(timestamp) : moment(timestamp)
  const _time = utc ? moment.utc(timestamp) : moment(timestamp)
  if (moment().diff(timestamp) > moment.duration(1, 'hours')) {
    return _time.calendar()
  } else {
    return _time.fromNow()
  }
}

export const capitalizeString = str => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const unCapitalizeString = str => {
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export const noWhiteSpace = str => {
  if (!str || str == null) {
    return
  }
  return str.trim().replace(/\s+/g, '').toLowerCase()
}

export const formatCountryName = name => {
  if (!name || name == null) {
    return
  }
  return name.trim().replace(/\s+/g, '_').toLowerCase() // Trim and replace spaces with underscores
}

// Pluralize string based on value. Optionally pass a "wordPluralized" param if the word's pluralized version is more complex than the addition of an "s" at the end
export const pluralize = (value, word, wordPluralized) => {
  if (!word || word == null) {
    return
  }
  // return (word + "s") unless value #default to the "zero" formatting uf falsy value passed in
  const pluralized =
    wordPluralized && wordPluralized != null ? wordPluralized : word + 's'
  return value && value === 1 ? word : pluralized
}

export const lowerCase = word => {
  return word && word != null && typeof word === 'string'
    ? word.toLowerCase()
    : word
}

export const formatThreatLevel = severity => {
  if (!severity || severity == null) {
    return
  }
  return severity >= SEVERITY_THRESHHOLDS.high
    ? 'high'
    : severity >= SEVERITY_THRESHHOLDS.medium ? 'medium' : 'low'
}

export const formatUrlGuy = urlString => {
  if (!urlString || urlString == null) {
    return
  }
  // New formatting, just return the raw string
  if (urlString.indexOf('Url(') === -1) {
    return urlString
  }
  // format old style
  const newStr = urlString.slice(4, -5)
  return newStr.split(',').join('')
}
